import { apiRequest } from '../../shared/api/client';

/**
 * License plates — mirrors Invetrak.Api.Endpoints.LicensePlateEndpoints (camelCase).
 *   GET  /api/v1/license-plates[?status&warehouseId&search&skip&take] -> { items, totalCount }
 *   GET  /api/v1/license-plates/{id}                                  -> LicensePlateDto
 *   GET  /api/v1/license-plates/{id}/contents                         -> LicensePlateContentDto[]
 *   POST /api/v1/license-plates          { count }                    -> LicensePlateDto[]
 *   PUT  /api/v1/license-plates/{id}/status { status }                -> LicensePlateDto
 * Reads need licenseplates:read, writes licenseplates:write.
 *
 * A plate is a printed number, so there is no rename and no delete: the number is fixed, and a
 * plate is retired rather than removed so the ledger rows referencing it keep pointing at
 * something. Status is the only mutable field.
 *
 * `locationId`/`locationCode`/`warehouseCode` are null in two different situations, and
 * `locationCount` is what tells them apart: 0 means the plate is empty so it is nowhere, and more
 * than 1 means it holds stock in several places so no single location is the answer. The server
 * withholds the single-location answer rather than picking one — the legacy system picked
 * arbitrarily and reported a half-truth.
 */

export type LicensePlateStatus = 'open' | 'closed' | 'disposed';

/** Matches usp_NextNumber_Reserve's own ceiling, so the UI refuses before the server has to. */
export const LICENSE_PLATE_MAX_BATCH = 1000;

export interface LicensePlateDto {
  licensePlateId: string;
  licensePlateNumber: string;
  status: LicensePlateStatus;
  createdAtUtc: string;
  closedAtUtc: string | null;
  itemCount: number;
  locationCount: number;
  locationId: string | null;
  locationCode: string | null;
  warehouseId: string | null;
  warehouseCode: string | null;
}

export interface LicensePlateContentDto {
  warehouseId: string;
  warehouseCode: string;
  locationId: string;
  locationCode: string;
  itemId: string;
  itemNumber: string;
  itemDescription: string;
  lotId: string | null;
  lotNumber: string | null;
  balanceTypeId: number;
  balanceTypeCode: string;
  balanceTypeName: string;
  quantity: number;
  uomId: string;
  uomCode: string;
}

export interface LicensePlatePage {
  items: LicensePlateDto[];
  totalCount: number;
}

export interface ListLicensePlatesOptions {
  status?: LicensePlateStatus;
  warehouseId?: string;
  search?: string;
  skip?: number;
  take?: number;
}

const COOKIE_INIT: RequestInit = { credentials: 'include' };

export async function listLicensePlates(
  options: ListLicensePlatesOptions = {},
  signal?: AbortSignal,
): Promise<LicensePlatePage> {
  const params = new URLSearchParams();
  if (options.status) params.set('status', options.status);
  if (options.warehouseId) params.set('warehouseId', options.warehouseId);
  if (options.search?.trim()) params.set('search', options.search.trim());
  if (options.skip !== undefined) params.set('skip', String(options.skip));
  if (options.take !== undefined) params.set('take', String(options.take));
  const query = params.toString();

  const { data } = await apiRequest<LicensePlatePage>(
    `/v1/license-plates/${query ? `?${query}` : ''}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function getLicensePlateContents(
  id: string,
  signal?: AbortSignal,
): Promise<LicensePlateContentDto[]> {
  const { data } = await apiRequest<LicensePlateContentDto[]>(
    `/v1/license-plates/${encodeURIComponent(id)}/contents`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/** Returns every plate created, in number order — one row when count is 1. */
export async function createLicensePlates(
  count: number,
  signal?: AbortSignal,
): Promise<LicensePlateDto[]> {
  const { data } = await apiRequest<LicensePlateDto[]>('/v1/license-plates/', {
    method: 'POST',
    body: { count },
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

/** Moves every line on the plate to another location, keeping it on the plate. */
export async function moveLicensePlate(
  id: string,
  toLocationId: string,
  reason?: string,
  signal?: AbortSignal,
): Promise<LicensePlateDto> {
  const { data } = await apiRequest<LicensePlateDto>(
    `/v1/license-plates/${encodeURIComponent(id)}/move`,
    { method: 'POST', body: { toLocationId, reason }, signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * Takes everything off the plate. Omit `toLocationId` to unload it where it stands — the ordinary
 * case, and something the legacy system refuses because its unload carries the move's
 * "destination must differ" guard.
 */
export async function unloadLicensePlate(
  id: string,
  toLocationId?: string,
  reason?: string,
  signal?: AbortSignal,
): Promise<LicensePlateDto> {
  const { data } = await apiRequest<LicensePlateDto>(
    `/v1/license-plates/${encodeURIComponent(id)}/unload`,
    { method: 'POST', body: { toLocationId: toLocationId ?? null, reason }, signal, ...COOKIE_INIT },
  );
  return data;
}

export async function setLicensePlateStatus(
  id: string,
  status: LicensePlateStatus,
  signal?: AbortSignal,
): Promise<LicensePlateDto> {
  const { data } = await apiRequest<LicensePlateDto>(
    `/v1/license-plates/${encodeURIComponent(id)}/status`,
    { method: 'PUT', body: { status }, signal, ...COOKIE_INIT },
  );
  return data;
}
